// WHAT: Custom hook for fetching roadmap items with loading and error states
// WHY:  Provides a standardized way to fetch roadmap items and update context state
// DOMAIN: Admin → Roadmap
// DEPENDENCIES: Tauri core API, useRoadmapContext hook
// RISKS:
//   - NOTE: Uses isMounted flag to prevent state updates after unmount
//   - NOTE: Error is converted to string - may lose error details
//   - NOTE: setItems dependency may cause unnecessary re-fetches

import { core } from "@tauri-apps/api"; // Tauri v2.5.1 Importstil
import { useEffect, useState } from "react";

import { useRoadmapContext } from "../context/useRoadmapContext";
import { type Roadmap_Item } from "../types/roadmap-types";

/**
 * Generic fetch status type
 */
interface FetchStatus<T> {
  /** Fetched data */
  data?: T;
  /** Error message if fetch failed */
  error?: string;
  /** Whether data is currently being fetched */
  loading: boolean;
}

/**
 * WHAT:
 *   Custom hook that fetches roadmap items and updates context state.
 *
 * WHY:
 *   Encapsulates roadmap item fetching logic with proper loading and error states,
 *   and automatically updates the roadmap context with fetched data.
 *
 * RETURNS:
 *   FetchStatus<Roadmap_Item[]> — Object with loading, error, and data states
 *
 * @returns {FetchStatus<Roadmap_Item[]>} Objekt mit loading, error und data States
 *
 * BEHAVIOR:
 *   - Fetches roadmap items on mount via Tauri command
 *   - Updates context state with fetched items
 *   - Manages loading and error states
 *   - Prevents state updates after component unmount
 *
 * RISKS:
 *   - NOTE: isMounted flag prevents memory leaks but may hide race conditions
 *   - NOTE: Error conversion to string may lose error type information
 *   - NOTE: setItems dependency may cause re-fetch if context provider re-renders
 *
 */
export const useRoadmapFetch = () => {
  // NOTE: Get setItems function from context to update global state
  const { setItems } = useRoadmapContext();
  // NOTE: Local state for fetch status
  const [status, setStatus] = useState<FetchStatus<Roadmap_Item[]>>({
    loading: true,
  });

  // NOTE: Fetch items on mount and update context
  useEffect(() => {
    // NOTE: isMounted flag prevents state updates after component unmount
    let isMounted = true;

    core
      .invoke<Roadmap_Item[]>("fetch_roadmap_items")
      .then((data) => {
        if (!isMounted) {
          return;
        }
        // NOTE: Update both context and local state
        setItems(data);
        setStatus({ data, loading: false });
      })
      .catch((error: unknown) => {
        if (!isMounted) {
          return;
        }
        setStatus({ error: String(error), loading: false });
      });

    // NOTE: Cleanup function sets isMounted to false
    return () => {
      isMounted = false;
    };
  }, [setItems]);

  return status;
};
